import { createAsyncThunk, createEntityAdapter, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import i18n from 'i18next';
import { BundleContentOverview, BundleOverview, LocalBundleEnvelope, PageMetadata } from '@cdaas/api-clients/dist/product-service-openapi';

import { RootState } from '.';
import { productServiceAdminBundlesClient, productServiceSalesChannelItemsClient } from '../api-client/product-service-client';
import { EntriesState, initialEntriesState, Loading } from '../utils/store';
import { setNotification } from './ui';
import { resolveAxiosErrorResponse } from '../utils/errors';

type BundleId = number;

interface AdminBundlesState extends EntriesState<BundleOverview> {
  pageMetadata: PageMetadata;
  variantsByBundle: Record<BundleId, BundleContentOverview>;
  salesChannelBundles: Record<BundleId, LocalBundleEnvelope>;
}

export interface BundleListItemType extends BundleOverview {
  content: BundleContentOverview;
}

export interface SalesChannelBundleListItemType extends BundleOverview {
  content: LocalBundleEnvelope;
}

export const initialAdminBundlesState: AdminBundlesState = {
  ...initialEntriesState,
  pageMetadata: {
    current_page: 0,
    size: 0,
    total_items: 0,
    total_pages: 0,
  },
  variantsByBundle: {},
  salesChannelBundles: {},
};

const adminBundlesAdapter = createEntityAdapter<BundleOverview>();

interface FetchAdminBundlesParams {
  goToPageNumber: number | undefined;
  numberOfItems: number | undefined;
  searchText: string | undefined;
}

export interface FetchAdminBundleProductsParams {
  goToPageNumber?: number;
  numberOfItems?: number;
  salesChannelUuid?: string;
  searchText?: string;
}

interface FetchAdminSalesChannelBundleParams {
  salesChannelUuid: string;
  bundleId: number;
}

// thunks
export const fetchAdminBundles = createAsyncThunk(
  'adminBundles/fetchAdminBundlesStatus',
  async ({ goToPageNumber, numberOfItems, searchText }: FetchAdminBundlesParams, { rejectWithValue, dispatch }) => {
    dispatch(clearVariantsByBundle());
    try {
      const { data } = await productServiceAdminBundlesClient.getAdminBundles(
        goToPageNumber?.toString(),
        numberOfItems?.toString(),
        undefined,
        searchText,
      );
      return data;
    } catch (error) {
      return rejectWithValue(resolveAxiosErrorResponse(error)?.statusText);
    }
  },
);

export const fetchBundleContent = createAsyncThunk('adminBundles/fetchBundleContentStatus', async (bundleId: BundleId) => {
  const response = await productServiceAdminBundlesClient.getAdminBundleContentOverview(bundleId);
  return {
    bundleId,
    bundleContent: response.data,
  };
});

export const fetchAdminBundlesBySalesChannelUuid = createAsyncThunk(
  'adminBundles/fetchAdminBundleProductsBySalesChannelUuidStatus',
  async ({ goToPageNumber, numberOfItems, salesChannelUuid, searchText }: FetchAdminBundleProductsParams, { rejectWithValue, dispatch }) => {
    dispatch(clearVariantsByBundle());
    if (salesChannelUuid) {
      try {
        const { data } = await productServiceAdminBundlesClient.getAdminSalesChannelBundles(
          salesChannelUuid,
          undefined,
          goToPageNumber?.toString(),
          numberOfItems?.toString(),
          undefined,
          searchText,
        );

        return data;
      } catch (error) {
        return rejectWithValue(resolveAxiosErrorResponse(error)?.statusText);
      }
    }
  },
);

export const fetchAdminSalesChannelBundle = createAsyncThunk(
  'adminBundles/fetchAdminSalesChannelStatus',
  async ({ salesChannelUuid, bundleId }: FetchAdminSalesChannelBundleParams, { rejectWithValue }) => {
    try {
      const { data } = await productServiceSalesChannelItemsClient.getSalesChannelBundle(salesChannelUuid, bundleId);

      return {
        bundleId,
        bundle: data,
      };
    } catch (error) {
      return rejectWithValue(resolveAxiosErrorResponse(error)?.statusText);
    }
  },
);

export const deleteBundles = createAsyncThunk('adminBundles/deleteBundlesStatus', async (bundleIds: BundleId[], { rejectWithValue, dispatch }) => {
  let errorBundle;
  try {
    for (const bundleId of bundleIds) {
      errorBundle = bundleId;
      const response = await productServiceAdminBundlesClient.deleteAdminBundle(bundleId);
      if (response.status === 204) {
        dispatch(removeOneAdminBundle(bundleId));
      }
    }
    const notificationText = i18n.t('bundlesview:notification.bundleDeleted', { count: bundleIds.length });
    dispatch(setNotification({ text: notificationText, show: true, type: 'success' }));
  } catch (error) {
    const axiosErrorResponse = resolveAxiosErrorResponse(error);
    const errorText = `Bundle ${errorBundle} ${axiosErrorResponse?.data?.error || ''}`;
    dispatch(
      setNotification({
        text: errorText,
        show: true,
        type: 'warning',
      }),
    );
    return rejectWithValue(axiosErrorResponse?.statusText);
  }
});

// slice
const adminBundles = createSlice({
  name: 'adminBundles',
  initialState: adminBundlesAdapter.getInitialState(initialAdminBundlesState),
  reducers: {
    removeOneAdminBundle(state, action: PayloadAction<BundleId>) {
      const bundleId = action.payload;
      adminBundlesAdapter.removeOne(state, bundleId);
      delete state.variantsByBundle[bundleId];
    },
    clearVariantsByBundle(state) {
      state.variantsByBundle = {};
    },
    addSalesChannelByUUID(state, action: PayloadAction<{ salesChannelUUID: string; bundleId: BundleId }>) {
      const { bundleId, salesChannelUUID } = action.payload;
      const bundle = state.entities[bundleId];
      if (bundle) {
        bundle.sales_channel_uuids = [...(bundle.sales_channel_uuids || []), salesChannelUUID];
      }
    },
    removeSalesChannelByUUID(state, action: PayloadAction<{ salesChannelUUID: string; bundleId: BundleId }>) {
      const { bundleId, salesChannelUUID } = action.payload;
      const bundle = state.entities[bundleId];
      if (bundle) {
        const sales_channel_uuids = [...(bundle.sales_channel_uuids || [])];
        bundle.sales_channel_uuids = sales_channel_uuids.filter(uuid => uuid !== salesChannelUUID);
      }
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchAdminBundles.pending, state => {
      state.loading = Loading.PENDING;
    });
    builder.addCase(fetchAdminBundles.fulfilled, (state, action) => {
      if (action.payload) {
        adminBundlesAdapter.setAll(state, action.payload.bundles);
        state.pageMetadata = action.payload.page_metadata;
        state.loading = Loading.SUCCEEDED;
      }
    });
    builder.addCase(fetchAdminBundles.rejected, state => {
      state.loading = Loading.FAILED;
    });

    builder.addCase(fetchBundleContent.fulfilled, (state, action) => {
      if (action.payload) {
        const { bundleId, bundleContent } = action.payload;
        state.variantsByBundle[bundleId] = bundleContent;
        state.loading = Loading.SUCCEEDED;
      }
    });
    builder.addCase(fetchAdminSalesChannelBundle.fulfilled, (state, action) => {
      if (action.payload) {
        const { bundleId, bundle } = action.payload;
        state.salesChannelBundles[bundleId] = bundle;
        state.loading = Loading.SUCCEEDED;
      }
    });
    builder.addCase(fetchAdminBundlesBySalesChannelUuid.pending, state => {
      state.loading = Loading.PENDING;
    });
    builder.addCase(fetchAdminBundlesBySalesChannelUuid.fulfilled, (state, action) => {
      if (action.payload) {
        adminBundlesAdapter.setAll(state, action.payload.bundles);
        state.pageMetadata = action.payload.page_metadata;
        state.loading = Loading.SUCCEEDED;
      }
    });
    builder.addCase(fetchAdminBundlesBySalesChannelUuid.rejected, state => {
      state.loading = Loading.FAILED;
    });
  },
});

export const {
  removeOneAdminBundle,
  clearVariantsByBundle,
  addSalesChannelByUUID: addSalesChannelById,
  removeSalesChannelByUUID: removeSalesChannelById,
} = adminBundles.actions;

export default adminBundles.reducer;

// selectors
export const { selectAll: selectAllBundles, selectById: selectBundleById } = adminBundlesAdapter.getSelectors(
  (state: RootState) => state.adminBundles,
);

export const selectAdminBundlesLoading = (state: RootState) => state.adminBundles.loading;
export const selectAdminBundlesPageMetadata = (state: RootState) => state.adminBundles.pageMetadata;

const selectAllVariantsByBundle = (state: RootState) => state.adminBundles.variantsByBundle;
export const selectAllAdminBundlesWithContent = createSelector([selectAllBundles, selectAllVariantsByBundle], (bundles, variantsByBundle) =>
  bundles.map(bundle => {
    const { id } = bundle;
    const content = variantsByBundle[id];
    return {
      ...bundle,
      content,
    };
  }),
);

const selectAllSalesChannelBundles = (state: RootState) => state.adminBundles.salesChannelBundles;
export const selectAllSalesChannelBundlesWithContent = createSelector(
  [selectAllBundles, selectAllSalesChannelBundles],
  (bundles, salesChannelBundles) =>
    bundles.map(bundle => {
      const { id } = bundle;
      const content = salesChannelBundles[id];
      return {
        ...bundle,
        content,
      };
    }),
);
